import type { PublisherConfig, SelfServiceFormData } from '../../../types/selfService';
import { US_STATE_MAP } from '../../../data/usStates';
import { resolveDmaName } from '../targetingRow';
import { formatTicketDetails } from './ticketContactInfo';

const DRAFT_CACHE_PREFIX = 'ctvbuyer.selfservice.draft.';

export const SNAPSHOT_START = '::ctv-campaign::';
export const SNAPSHOT_END = '::/ctv-campaign::';

export interface TicketCampaignSnapshot {
  businessName?: string;
  businessWebsite?: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  startDate?: string;
  endDate?: string;
  budget?: number;
  /** Human-readable custom geo summary; omitted when targetingMode is default. */
  targeting?: string;
  paymentPlan?: 'upfront' | 'monthly';
}

export function readWizardDraftFormData(slug: string): Partial<SelfServiceFormData> {
  try {
    const raw = localStorage.getItem(`${DRAFT_CACHE_PREFIX}${slug}.cache`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { formData?: Partial<SelfServiceFormData> };
    return parsed.formData ?? {};
  } catch {
    return {};
  }
}

function trimField(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function buildCustomTargetingSummary(
  form: Partial<SelfServiceFormData>,
  config?: PublisherConfig,
): string | undefined {
  if ((form.targetingMode ?? 'default') !== 'custom') return undefined;

  const geoMode = form.geoMode ?? 'radius';
  if (geoMode === 'states') {
    const states = form.selectedStates ?? [];
    if (states.length === 0) return undefined;
    const names = states.map((code) => US_STATE_MAP[code] ?? code);
    return names.length <= 3 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  }

  if (geoMode === 'dmas') {
    const dmas = form.selectedDMAs ?? [];
    if (dmas.length === 0) return undefined;
    const names = dmas.map((code) => {
      const name = resolveDmaName(config, code);
      // Preserve the "Metro <code>" shape for codes the Nielsen list doesn't
      // know either, so the snapshot never shows a bare number.
      return name === code ? `Metro ${code}` : name;
    });
    return names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1} more`;
  }

  const zips = form.geoTargetZips ?? [];
  const miles = form.geoTargetRadiusMiles;
  const label = form.geoTargetCenter?.label?.trim();
  if (zips.length > 0 || label) {
    const radiusPart =
      miles != null ? `${miles}mi radius` : 'Custom radius';
    const zipPart = zips.length > 0 ? ` · ${zips.length} ZIP${zips.length === 1 ? '' : 's'}` : '';
    const labelPart = label ? ` · ${label}` : '';
    return `${radiusPart}${zipPart}${labelPart}`;
  }

  return undefined;
}

export function buildTicketCampaignSnapshot(
  form: Partial<SelfServiceFormData>,
  options: { includePayment: boolean },
  config?: PublisherConfig,
): TicketCampaignSnapshot | null {
  const snapshot: TicketCampaignSnapshot = {};

  const businessName = trimField(form.businessName);
  const businessWebsite = trimField(form.businessWebsite);
  const contactEmail = trimField(form.contactEmail);
  const contactName = trimField(form.contactName);
  const contactPhone = trimField(form.contactPhone);
  const startDate = trimField(form.startDate);
  const endDate = trimField(form.endDate);

  if (businessName) snapshot.businessName = businessName;
  if (businessWebsite) snapshot.businessWebsite = businessWebsite;
  if (contactEmail) snapshot.contactEmail = contactEmail;
  if (contactName) snapshot.contactName = contactName;
  if (contactPhone) snapshot.contactPhone = contactPhone;
  if (startDate) snapshot.startDate = startDate;
  if (endDate) snapshot.endDate = endDate;

  const budget = form.budget;
  if (typeof budget === 'number' && budget > 0) {
    snapshot.budget = budget;
  }

  const targeting = buildCustomTargetingSummary(form, config);
  if (targeting) snapshot.targeting = targeting;

  if (options.includePayment && form.paymentPlan) {
    snapshot.paymentPlan = form.paymentPlan;
  }

  return Object.keys(snapshot).length > 0 ? snapshot : null;
}

export function peelSnapshotBlock(details: string): {
  snapshot: TicketCampaignSnapshot | null;
  remaining: string;
} {
  const pattern = new RegExp(
    `^${SNAPSHOT_START}\\n([\\s\\S]*?)\\n${SNAPSHOT_END}\\n\\n?`,
  );
  const match = details.match(pattern);
  if (!match) {
    return { snapshot: null, remaining: details };
  }

  let snapshot: TicketCampaignSnapshot | null = null;
  try {
    snapshot = JSON.parse(match[1]) as TicketCampaignSnapshot;
  } catch {
    snapshot = null;
  }

  return { snapshot, remaining: details.slice(match[0].length) };
}

export function parseTicketCampaignSnapshot(details: string): TicketCampaignSnapshot | null {
  return peelSnapshotBlock(details).snapshot;
}

export function encodeTicketDetailsWithSnapshot(
  snapshot: TicketCampaignSnapshot | null,
  campaignName: string,
  userDetails: string,
  issueTitle?: string,
): string {
  const parts: string[] = [];
  if (snapshot) {
    parts.push(`${SNAPSHOT_START}\n${JSON.stringify(snapshot)}\n${SNAPSHOT_END}`);
  }
  parts.push(formatTicketDetails(campaignName, userDetails, issueTitle));
  return parts.join('\n\n');
}
